<script lang="ts" setup>
import { onMounted, reactive } from 'vue'
import { Product } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import ProductTable from '@/components/dashboard/Admin/AdminProduct/ProductTable.vue'
import { useProductsStore } from '@/stores'
import { STATUS_CODES } from '@/constants'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'

const data = reactive({
  loading: false,
  products: [] as Product[],
  mutable: [] as Product[],
  immutable: [] as Product[]
})

const loadProducts = (forceRefresh = false) => {
  data.loading = true
  const productsStore = useProductsStore()

  // Use cache only if valid and not forcing refresh
  if (!forceRefresh && productsStore.isCacheValid()) {
    data.products = Array.from(productsStore.getProducts)
    if (data.products.length > 0) {
      sortProducts()
      data.loading = false
      return
    }
  }

  useAxios<null, Array<Product>>('GET', '/product', true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        const validate = Product.array().safeParse(res.data)
        if (!validate.success) return

        productsStore.setProducts(Array.from(validate.data))
        data.products = Array.from(validate.data)
        sortProducts()
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'ProductManagementHome' })
    })
    .finally(() => {
      data.loading = false
    })
}

const handleRefresh = () => {
  loadProducts(true)
}

const sortProducts = () => {
  data.mutable = []
  data.immutable = []
  data.products.forEach((prod) => {
    if (prod.slug) {
      if (prod.immutable) {
        data.immutable.push(prod)
      } else {
        data.mutable.push(prod)
      }
    }
  })
}

onMounted(() => {
  loadProducts()
})
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.products.title')"
    :subtitle="$t('dashboard.admin.products.subtitle')"
    icon="mdi-package-variant-closed-plus"
    icon-color="teal"
  >
    <div v-if="data.loading" class="loading-container">
      <div class="loading-spinner">
        <v-progress-circular color="cyan" indeterminate size="48" width="4" />
        <span class="loading-text">{{ $t('dashboard.admin.products.loading') }}</span>
      </div>
    </div>

    <template v-else>
      <ProductTable
        :products="data.mutable"
        @refresh="handleRefresh"
      />

      <ProductTable
        :products="data.immutable"
        :title="$t('dashboard.admin.products.system')"
        :subtitle="$t('dashboard.admin.products.systemSubtitle')"
        immutable
        default-collapsed
      />
    </template>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Loading Container */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}
</style>
